import { JSX, useState } from 'react';
import { EmailListItemProps, LIST_ITEM_TOOLTIPS } from './types';
import styles from './emailItem.module.less';
import { Badge, Divider, Popconfirm, Typography } from 'antd';
import { CloseOutlined, LinkedinFilled } from '@ant-design/icons';
import {
    EMAIL_ITEM_CONTAINER_TEST_ID,
    EMAIL_ITEM_DELETE_TEST_ID,
    EMAIL_ITEM_LINKEDIN_TEST_ID,
    EMAIL_ITEM_REMINDER_TEST_ID,
} from './constants';
import { ContactAvatar } from '../../../../components/componentModels/activities/conversationWrapper/item/contact-avatar';
import DropdownReminderSelector from '../../../../components/selector/reminder/dropdownSelector/dropdownReminderSelector';
import ButtonWithTooltip from '../../../../components/selector/reminder/dropdownSelector/buttonToolTip';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import {
    INBOX_DELETE_TRACKING_ID,
    INBOX_OPEN_DRAWER_TRACKING_ID,
    INBOX_SET_REMINDER_TRACKING_ID,
    INBOX_VIEW_LI_PROFILE_TRACKING_ID,
} from '@copilot/common/tracking/userpilotEventConsts';
import AutoReplyStatusIndicator from '../autoReply/autoReplyStatusIndicator';
import { useFeatureFlags } from '@copilot/common/hooks/useFeatureFlags/useFeatureFlags';
import { useAutoReplyToggle } from '@copilot/common/hooks/replyRecommendation/useAutoReply';
import isEqual from 'lodash/isEqual';

const { Text } = Typography;

/**
 * [Presentational] Component used to display a message from a prospect in the inbox
 */
export function EmailItem({
    profileUrl,
    timestamp,
    isSelected,
    isDeclined,
    onClick,
    onDelete: handleDelete,
    reminder: {
        hasReminder: initReminderState,
        onCancelReminder: handleReminderCancelled,
        onSetReminder: handleReminderSet,
        onReminderClicked,
        reminderTimestamp,
    },
    inboxMessage,
}: EmailListItemProps): JSX.Element {
    const [isReminderLoading, setIsReminderLoading] = useState(false);
    const [hasReminder, setHasReminder] = useState(initReminderState);
    const [isDeleting, setIsDeleting] = useState(false);
    const { isAutoReplyBetaV1Enabled } = useFeatureFlags();
    const { lastReceived, lastMessage, contactName, isRead } = inboxMessage;
    const isLastMessageReceived = isEqual(lastReceived, lastMessage);

    const {
        isPaused: isAutoReplyPaused,
        isEnabled: isAutoReplyEnabled,
        isUpdating: isAutoReplyUpdating,
        isIndicatorShown: isAutoReplyIndicatorShown,
    } = useAutoReplyToggle({
        isEnabledForCampaign: inboxMessage.autoReply.isEnabledForCampaign,
        isEnabledForConnection: inboxMessage.autoReply.isEnabledForConnection,
        isLastMessageReceived,
        autoReplyStatus: inboxMessage.replyRecommendation?.metaData?.autoReplyStatus,
    });

    const displayedMessage = isLastMessageReceived
        ? lastMessage.message
        : `You replied: ${lastMessage.message}`;

    async function onCancelReminder() {
        setIsReminderLoading(true);
        await handleReminderCancelled();
        setHasReminder(false);
        setIsReminderLoading(false);
    }

    async function onSetReminder(date: moment.Moment, isNewReminder: boolean) {
        setIsReminderLoading(true);
        await handleReminderSet(date, isNewReminder);
        setHasReminder(true);
        setIsReminderLoading(false);
    }

    async function onDelete() {
        setIsDeleting(true);
        await handleDelete();
        setIsDeleting(false);
    }

    return (
        <div
            onClick={onClick}
            className={[styles.emailItem, isSelected ? styles.selected : ''].join(' ')}
            data-testid={EMAIL_ITEM_CONTAINER_TEST_ID}
            data-tracking-id={INBOX_OPEN_DRAWER_TRACKING_ID}
        >
            <div className={styles.messageWrapper}>
                <ContactAvatar type="inbox" contactName={contactName} />

                <div className={styles.messageContentWrapper}>
                    <Text className={styles.name} title={contactName} strong>
                        {contactName}
                    </Text>
                    <Divider type="vertical" className={styles.divider} />
                    <Text
                        className={[styles.message, isRead ? styles.read : ''].join(' ')}
                        strong={!isRead}
                        title={displayedMessage}
                    >
                        {isDeclined ? `Declined - ${displayedMessage}` : displayedMessage}
                    </Text>
                </div>
            </div>

            <div className={styles.actionWrapper} onClick={(event) => event.stopPropagation()}>
                {isAutoReplyBetaV1Enabled && isAutoReplyIndicatorShown && (
                    <AutoReplyStatusIndicator
                        isEnabled={isAutoReplyEnabled}
                        isPaused={isAutoReplyPaused}
                        isUpdating={isAutoReplyUpdating}
                        hideToggle
                    />
                )}

                <Text className={styles.timestamp}>{timestamp}</Text>

                <ButtonWithTooltip
                    isIconOnly
                    label={
                        isEmpty(profileUrl)
                            ? LIST_ITEM_TOOLTIPS.LINKED_IN_NOT_ACCESSIBLE
                            : LIST_ITEM_TOOLTIPS.VIEW_LINKEDIN
                    }
                    size="middle"
                    type="text"
                    icon={<LinkedinFilled />}
                    disabled={isEmpty(profileUrl)}
                    href={profileUrl}
                    target="_blank"
                    referrerPolicy="no-referrer"
                    data-testid={EMAIL_ITEM_LINKEDIN_TEST_ID}
                    data-tracking-id={INBOX_VIEW_LI_PROFILE_TRACKING_ID}
                />

                <Badge
                    dot={hasReminder}
                    data-testid={EMAIL_ITEM_REMINDER_TEST_ID}
                    data-tracking-id={INBOX_SET_REMINDER_TRACKING_ID}
                >
                    {/* TODO > Populate reminder related functionality when list logic exists */}
                    <DropdownReminderSelector
                        buttonType="text"
                        isIconOnly={true}
                        isReminderSet={hasReminder}
                        onCancel={() => void onCancelReminder()}
                        onUpdate={(date, isNewReminder) => void onSetReminder(date, isNewReminder)}
                        onSetReminderClick={onReminderClicked}
                        loading={isReminderLoading}
                        selectedDate={hasReminder ? moment(reminderTimestamp) : undefined}
                    />
                </Badge>

                <Popconfirm
                    title={
                        <Text>
                            This message will be removed from your Inbox. <br /> You can still find
                            it in the Connections page.
                        </Text>
                    }
                    placement={'left'}
                    okText="Ok"
                    onConfirm={() => void onDelete()}
                >
                    <ButtonWithTooltip
                        type="text"
                        isIconOnly
                        label="Remove from inbox"
                        icon={<CloseOutlined />}
                        loading={isDeleting}
                        data-testid={EMAIL_ITEM_DELETE_TEST_ID}
                        data-tracking-id={INBOX_DELETE_TRACKING_ID}
                        size="middle"
                    />
                </Popconfirm>
            </div>
        </div>
    );
}
